import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { SectionHeading } from "./Section";
import { StaggerItem, Stagger } from "./Reveal";
import { CLINIC_PHOTOS, STOCK } from "../data/site";

const GALLERY = [
  { src: CLINIC_PHOTOS.building, cat: "Clinic", label: "DASH Clinic — By Pass Road, Belpahar" },
  { src: CLINIC_PHOTOS.signboard, cat: "Clinic", label: "Clinic Signboard & Services" },
  { src: CLINIC_PHOTOS.reception, cat: "Facilities", label: "Reception & Waiting Area" },
  { src: CLINIC_PHOTOS.pediatricTherapy, cat: "Treatment", label: "Pediatric Physiotherapy Session" },
  { src: CLINIC_PHOTOS.pediatricRoom, cat: "Facilities", label: "Children's Therapy Room" },
  { src: STOCK.physioManual, cat: "Physiotherapy", label: "Manual Therapy" },
  { src: STOCK.backPain, cat: "Physiotherapy", label: "Back & Neck Care" },
  { src: STOCK.audiology, cat: "Equipment", label: "Audiology Assessment" },
];

const CATS = ["All", "Clinic", "Physiotherapy", "Equipment", "Treatment", "Facilities"];

export const Gallery = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const filtered = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  const go = (dir) => {
    if (active === null) return;
    const n = (active + dir + filtered.length) % filtered.length;
    setActive(n);
  };

  return (
    <section className="py-24 md:py-32 bg-dash-bg" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="Clinic Gallery" title="Inside DASH Clinic" subtitle="A look at our clinic space, therapy rooms and facilities in Belpahar." />

        <div className="mt-10 flex flex-wrap gap-2.5">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ${
                cat === c ? "bg-dash-ink text-white" : "bg-white text-dash-muted border border-dash-line hover:border-dash-teal hover:text-dash-teal"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <Stagger className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((g, i) => (
            <StaggerItem key={g.src + i} className={i % 5 === 0 ? "md:row-span-2" : ""}>
              <button
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
                className={`group relative w-full overflow-hidden rounded-3xl bg-dash-ink/5 ${i % 5 === 0 ? "aspect-[3/4]" : "aspect-square"}`}
              >
                <img src={g.src} alt={g.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-dash-ink/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-400 text-left">
                  <span className="text-[10px] uppercase tracking-widest text-dash-teal font-accent font-semibold">{g.cat}</span>
                  <p className="text-white text-sm font-medium">{g.label}</p>
                </div>
              </button>
            </StaggerItem>
          ))}
        </Stagger>

        <p className="mt-8 flex items-center gap-2 text-sm text-dash-muted">
          <ImageOff className="h-4 w-4 text-dash-teal" />
          Real clinic photographs can be added and replaced here at any time.
        </p>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-dash-ink/95 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 text-white inline-flex items-center justify-center" onClick={() => setActive(null)} data-testid="lightbox-close">
              <X className="h-5 w-5" />
            </button>
            <button className="absolute left-4 md:left-8 h-12 w-12 rounded-full bg-white/10 text-white inline-flex items-center justify-center" onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="lightbox-prev">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button className="absolute right-4 md:right-8 h-12 w-12 rounded-full bg-white/10 text-white inline-flex items-center justify-center" onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="lightbox-next">
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.figure
              key={active}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}
            >
              <img src={filtered[active].src} alt={filtered[active].label} className="w-full max-h-[78vh] object-contain rounded-2xl" />
              <figcaption className="text-center text-white/80 mt-4 font-accent">{filtered[active].label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
